import { Box, Dialog, DialogProps, IconButton, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useScopelessHotkeys } from 'lib/hooks'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1.5),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1.5),
    top: theme.spacing(1.5),
    color: theme.palette.grey[500],
    // react-virtualized's AutoSizer makes the content box below position: relative, which would
    // then be painted over the button and swallow its clicks
    zIndex: 1,
  },
}))

export interface ClosableDialogProps extends DialogProps {
  onClose: (event: any, reason: 'backdropClick' | 'escapeKeyDown') => void
  onEnter?: () => void
  hideButton?: boolean
}

export const ClosableDialog = ({
  onClose,
  onEnter,
  children,
  hideButton,
  ...props
}: ClosableDialogProps): JSX.Element => {
  const classes = useStyles()

  if (onEnter) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useScopelessHotkeys(
      'enter',
      (e) => {
        e.preventDefault()
        e.stopPropagation()

        onEnter()
      },
      [onEnter]
    )
  }

  return (
    <Dialog
      disableEnforceFocus
      {...props}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') onClose(event, reason)
      }}
    >
      {!hideButton && onClose && (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={() => onClose({}, 'escapeKeyDown')}
          data-testid="close-btn-closableDialog"
        >
          <CloseIcon />
        </IconButton>
      )}
      <Box
        paddingX={{ xs: 2, sm: 4 }}
        paddingBottom={{ xs: 2, sm: 4 }}
        // on mobile the content would start under the absolute-positioned close button
        paddingTop={!hideButton && onClose ? { xs: 7, sm: 4 } : { xs: 2, sm: 4 }}
        minWidth={{ xs: 0, sm: '400px' }}
      >
        {children}
      </Box>
    </Dialog>
  )
}
